import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export type DbProduct = {
  id: string;
  name: string;
  brand: string;
  category: string;
  price: number;
  original_price: number;
  discount: number;
  image: string | null;
  images: string[] | null;
  rating: number;
  reviews: number;
  description: string | null;
  specs: Record<string, string>;
  in_stock: boolean;
  is_flash_sale: boolean;
  is_featured: boolean;
  colors: string[] | null;
  storages: string[] | null;
  rams: string[] | null;
  created_at: string;
  updated_at: string;
};

export const useProducts = (category?: string) => {
  return useQuery({
    queryKey: ['products', category],
    queryFn: async () => {
      let query = supabase.from('products').select('*').order('created_at', { ascending: false });
      if (category && category !== 'all') {
        query = query.eq('category', category);
      }
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as DbProduct[];
    },
  });
};

export const useProduct = (id: string) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('products').select('*').eq('id', id).single();
      if (error) throw error;
      return data as DbProduct;
    },
    enabled: !!id,
  });
};

export const useFeaturedProducts = () => {
  return useQuery({
    queryKey: ['products', 'featured'],
    queryFn: async () => {
      const { data, error } = await supabase.from('products').select('*').eq('is_featured', true);
      if (error) throw error;
      return (data ?? []) as DbProduct[];
    },
  });
};

export const useFlashSaleProducts = () => {
  return useQuery({
    queryKey: ['products', 'flash-sale'],
    queryFn: async () => {
      const { data, error } = await supabase.from('products').select('*').eq('is_flash_sale', true);
      if (error) throw error;
      return (data ?? []) as DbProduct[];
    },
  });
};

export const useDealsProducts = () => {
  return useQuery({
    queryKey: ['products', 'deals'],
    queryFn: async () => {
      const { data, error } = await supabase.from('products').select('*').gt('discount', 0).order('discount', { ascending: false });
      if (error) throw error;
      return (data ?? []) as DbProduct[];
    },
  });
};

export const useTopRatedProducts = () => {
  return useQuery({
    queryKey: ['products', 'top-rated'],
    queryFn: async () => {
      const { data, error } = await supabase.from('products').select('*').gt('rating', 0).order('rating', { ascending: false }).limit(8);
      if (error) throw error;
      return (data ?? []) as DbProduct[];
    },
  });
};

export const useOnSaleProducts = () => {
  return useQuery({
    queryKey: ['products', 'on-sale'],
    queryFn: async () => {
      const { data, error } = await supabase.from('products').select('*').gt('discount', 0).order('discount', { ascending: false }).limit(8);
      if (error) throw error;
      return (data ?? []) as DbProduct[];
    },
  });
};
